import { useQuery } from "@apollo/client";
import { GAMES_WITH_ASSIGNMENT, GET_ALL_BRANDS } from "../../graphql/games";
import { GameItem } from "../../components/ui/gameItem";
import { useEffect, useLayoutEffect, useState } from "react";
import { ReactComponent as ArrowRight } from "../../assets/icons/arrowRight.svg";
import LoaderOverlay from "../../components/ui/LoaderOverlay";
import { CustomSelect } from "../../components/ui/CustomSelect";
import useMainStore from "../../store/mainState";
import { useTranslation } from "react-i18next";

type pageInfo = {
    startCursor: string;
    endCursor: string;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    total: number;
}

const getPageRange = (current: number, total: number): (number | '...')[] => {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages: (number | '...')[] = [1];
    const left = Math.max(2, current - 1);
    const right = Math.min(total - 1, current + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < total - 1) pages.push('...');
    pages.push(total);
    return pages;
}

export const AllGamesByCategory = ({ title, categoryType = "" }: any) => {
    const { t } = useTranslation();
    const { environment, labelData } = useMainStore();
    let variables: any = {
        labelId: +labelData?.id,
        environmentId: environment?.id,
        isActive: true,
        first: 30,
        after: 0,
    }
    if (categoryType) {
        variables.categoryType = categoryType;
    }


    const { data: gamesDataWithAssignment, loading: loadGamesWithAssignment, error: loadGamesWithAssignmentError, refetch: refetchGamesWithAssignment } = useQuery(GAMES_WITH_ASSIGNMENT, {
        fetchPolicy: 'cache-and-network',
        variables,
        skip: !labelData?.id || !environment?.id,
    });
    const { data: brandsData, loading: loadBrands, error: loadBrandsError } = useQuery(GET_ALL_BRANDS, {
        fetchPolicy: 'cache-and-network',
        variables: {
            categoryType: categoryType,
            labelId: labelData?.id ? +labelData.id : undefined
        },
        skip: !labelData?.id
    });
    const [hasPagination, setHasPagination] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [countOfPage, setCountOfPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [games, setGames] = useState([]);
    const [pageInfo, setPageInfo] = useState<pageInfo | null>(null)
    const [comment, setComment] = useState('');
    const [brands, setBrands] = useState([]);
    const [brand, setBrand] = useState('');

    useEffect(() => {
        if (brandsData) {
            setBrands(
                brandsData?.getBrandNames?.map((brand: any) => {
                    return {
                        value: brand,
                        label: brand
                    }
                }))
        }
    }, [brandsData])

    useEffect(() => {
        if (gamesDataWithAssignment?.gamesWithAssignment) {
            setPageInfo(gamesDataWithAssignment?.gamesWithAssignment?.pageInfo);
            setGames(gamesDataWithAssignment?.gamesWithAssignment?.games);
        }
    }, [gamesDataWithAssignment]);
    useEffect(() => {
        if (pageInfo && pageInfo?.total / 30 > 1) {
            setHasPagination(true);
            setCountOfPage(Math.ceil(pageInfo?.total / 30));
        } else {
            setHasPagination(false)
        }
    }, [pageInfo])


    useEffect(() => {
        (async () => {
            try {
                console.log('Brand changed:', { brand, comment });
                let newVariables: any = {
                    labelId: +labelData?.id,
                    environmentId: environment?.id,
                    isActive: true,
                    first: 30,
                    after: 0,
                }
                if (comment) {
                    newVariables.comment = comment
                } else {
                    newVariables.comment = ""
                }
                if (brand) {
                    newVariables.brandname = brand
                } else {
                    newVariables.brandname = ""
                }
                if (categoryType) {
                    newVariables.categoryType = categoryType;
                }
                setLoading(true)
                console.log({ newVariables });

                const { data } = await refetchGamesWithAssignment(newVariables);
                console.log({ data });

                setGames(data?.gamesWithAssignment?.games);
                setLoading(false);
                setCountOfPage(0)
                setPageInfo(data?.gamesWithAssignment?.pageInfo);
            } catch (error) {
                console.log({ error })
                setLoading(false)
            }
        })()

    }, [comment, brand])

    const onPageChange = async (page: number) => {

        try {
            setLoading(true);
            setCurrentPage(page);
            let variables: any = {
                labelId: +labelData?.id,
                environmentId: environment?.id,
                isActive: true,
                first: 30,
                after: (page - 1) * 30,
            }
            if (comment) {
                variables.comment = comment
            } else {
                variables.comment = ""
            }
            if (brand) {
                variables.brandname = brand
            } else {
                variables.brandname = ""
            }

            const { data } = await refetchGamesWithAssignment(variables);
            setGames(data?.gamesWithAssignment?.games);
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    }

    // Show error state
    if (loadGamesWithAssignmentError) {
        return (
            <div className="mt-[35px]">
                <h1 className="text-[14px] lg:text-[20px] font-bold text-white mb-[15px]">{title}</h1>
                <div className="flex items-center justify-center py-12">
                    <p className="text-red-400 text-center">
                        {t('casino.failedToLoad')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-[35px] relative">
            <div className="flex flex-row items-center gap-[10px] w-[100%] md:w-[60%] mb-[40px]">
                <CustomSelect
                    label={t('casino.sortBy')}
                    value={comment}
                    onChange={setComment}
                    placeholder={t('casino.all')}
                    options={[
                        { value: "", label: t('casino.all') },
                        { value: "NEW", label: t('casino.new') },
                        { value: "HOT", label: t('casino.hot') },
                        { value: "PRO", label: t('casino.pro') }
                    ]}
                />
                <CustomSelect
                    label={t('casino.providers')}
                    value={brand}
                    onChange={setBrand}
                    placeholder={t('casino.all')}
                    options={[{ value: "", label: t('casino.all') }, ...brands]}
                />
            </div>
            <h1 className="text-[14px] lg:text-[20px]  font-bold text-white mb-[15px]">{title}</h1>
            <div className="flex flex-row flex-wrap  justify-between">
                {games?.map((game: any) => (
                    <GameItem game={game} key={game.id} />
                ))}
                {hasPagination && (
                    <div className="w-full flex flex-wrap items-center justify-center gap-[6px] sm:gap-[8px] mt-[20px]">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => onPageChange(currentPage - 1)}
                            className={`w-[36px] h-[36px] sm:w-[40px] sm:h-[40px] flex items-center justify-center bg-white/[0.06] border border-white/[0.08] hover:bg-white/[0.12] text-white rounded-[10px] transition-all ${currentPage === 1 ? 'opacity-40 cursor-not-allowed' : ''}`}
                            aria-label="Previous page"
                        >
                            <span className="rotate-180 inline-flex"><ArrowRight /></span>
                        </button>
                        {getPageRange(currentPage, countOfPage).map((page, idx) => (
                            page === '...' ? (
                                <span key={`dots-${idx}`} className="text-white/40 px-[4px] select-none">…</span>
                            ) : (
                                <button
                                    key={page}
                                    onClick={() => onPageChange(page as number)}
                                    className={`min-w-[36px] h-[36px] sm:min-w-[40px] sm:h-[40px] px-[10px] flex items-center justify-center text-[13px] sm:text-[14px] font-semibold rounded-[10px] transition-all border ${currentPage === page ? 'bg-gradient-to-r from-[#4DBDFF] to-[#0086D3] text-white border-transparent shadow-[0_2px_8px_rgba(0,134,211,0.3)]' : 'bg-white/[0.06] text-white/80 border-white/[0.08] hover:bg-white/[0.12] hover:text-white'}`}
                                >
                                    {page}
                                </button>
                            )
                        ))}
                        <button
                            disabled={currentPage === countOfPage}
                            onClick={() => onPageChange(currentPage + 1)}
                            className={`w-[36px] h-[36px] sm:w-[40px] sm:h-[40px] flex items-center justify-center bg-white/[0.06] border border-white/[0.08] hover:bg-white/[0.12] text-white rounded-[10px] transition-all ${currentPage === countOfPage ? 'opacity-40 cursor-not-allowed' : ''}`}
                            aria-label="Next page"
                        >
                            <ArrowRight />
                        </button>
                    </div>
                )}
            </div>

            {/* Pagination Loading Overlay */}
            <LoaderOverlay
                isVisible={loading}
                backdrop="dark"
                variant="spinner"
                size="md"
                color="white"
            />
        </div>
    )
}